import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faChevronLeft,
  faChevronRight,
  faEnvelopeOpenText,
  faPaperPlane,
  faCircleCheck,
  faTriangleExclamation,
  faClock,
  faEye,
  faChartColumn,
} from '@fortawesome/free-solid-svg-icons';
import { getMessageStatsAll } from '../../../lib/api';
import '../../dashboard/styles/dashboard.css';
import './admin.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const PAGE_SIZE = 1000;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'overall', label: 'Overall' },
];

const ACCENT = '#00a884';
const CHART_MUTED = 'rgba(100, 116, 139, 0.35)';

const formatInt = (v) => {
  const n = Number(v);
  if (Number.isNaN(n) || v === '' || v == null) return '—';
  return new Intl.NumberFormat('en-IN').format(n);
};

const pickNum = (row, snakeKey) => {
  const camel = snakeKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const raw = row[snakeKey] ?? row[camel];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const userLabel = (row) => {
  const s = String(row.name || row.email || (row.user_id != null ? `#${row.user_id}` : '—')).trim();
  return s.length > 26 ? `${s.slice(0, 24)}…` : s;
};

const doughnutSafe = (a, b) => {
  const x = Math.max(0, a);
  const y = Math.max(0, b);
  if (x === 0 && y === 0) return [1, 0];
  return [x, y];
};

const OverviewStatCard = ({ title, value, subtitle, segments }) => (
  <div className="dashboard-stat-card">
    <div className="dashboard-stat-content">
      <div className="dashboard-stat-text">
        <p className="dashboard-stat-title">{title}</p>
        <h2 className="dashboard-stat-value">{value}</h2>
        <p className="dashboard-stat-trend">{subtitle}</p>
      </div>
      <div className="dashboard-stat-chart">
        <Doughnut
          data={{
            labels: ['', ''],
            datasets: [
              {
                data: segments,
                backgroundColor: ['rgba(255,255,255,0.92)', 'rgba(255,255,255,0.22)'],
                borderWidth: 0,
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            cutout: '70%',
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
          }}
        />
      </div>
    </div>
  </div>
);

const MessageStatsOverview = () => {
  const [period, setPeriod] = useState('today');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const sums = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        total: acc.total + pickNum(r, 'total'),
        read: acc.read + pickNum(r, 'read'),
        sent: acc.sent + pickNum(r, 'sent'),
        delivered: acc.delivered + pickNum(r, 'delivered'),
        failed: acc.failed + pickNum(r, 'failed'),
        pending: acc.pending + pickNum(r, 'pending'),
        delivery_failed: acc.delivery_failed + pickNum(r, 'delivery_failed'),
      }),
      { total: 0, read: 0, sent: 0, delivered: 0, failed: 0, pending: 0, delivery_failed: 0 }
    );
  }, [rows]);

  const pipeline = sums.sent + sums.pending;
  const failures = sums.failed + sums.delivery_failed;

  const topUsersByTotal = useMemo(() => {
    return [...rows]
      .sort((a, b) => pickNum(b, 'total') - pickNum(a, 'total'))
      .slice(0, 14);
  }, [rows]);

  const userBarData = useMemo(
    () => ({
      labels: topUsersByTotal.map(userLabel),
      datasets: [
        {
          label: 'Messages',
          data: topUsersByTotal.map((r) => pickNum(r, 'total')),
          backgroundColor: ACCENT,
          borderRadius: 6,
          borderWidth: 0,
        },
      ],
    }),
    [topUsersByTotal]
  );

  const statusDoughnutData = useMemo(() => {
    const parts = [
      { key: 'read', label: 'Read', v: sums.read, color: '#38bdf8' },
      { key: 'sent', label: 'Sent', v: sums.sent, color: '#a78bfa' },
      { key: 'delivered', label: 'Delivered', v: sums.delivered, color: ACCENT },
      { key: 'failed', label: 'Failed', v: sums.failed, color: '#f87171' },
      { key: 'pending', label: 'Pending', v: sums.pending, color: '#fbbf24' },
      { key: 'delivery_failed', label: 'Delivery failed', v: sums.delivery_failed, color: '#fb7185' },
    ].filter((p) => p.v > 0);
    if (!parts.length) {
      return {
        labels: ['No data'],
        datasets: [{ data: [1], backgroundColor: [CHART_MUTED], borderWidth: 0 }],
      };
    }
    return {
      labels: parts.map((p) => p.label),
      datasets: [
        {
          data: parts.map((p) => p.v),
          backgroundColor: parts.map((p) => p.color),
          borderWidth: 0,
        },
      ],
    };
  }, [sums]);

  const statusBarData = useMemo(
    () => ({
      labels: ['Read', 'Sent', 'Delivered', 'Failed', 'Pending', 'Del. failed'],
      datasets: [
        {
          label: 'Count',
          data: [sums.read, sums.sent, sums.delivered, sums.failed, sums.pending, sums.delivery_failed],
          backgroundColor: ['#38bdf8', '#a78bfa', ACCENT, '#f87171', '#fbbf24', '#fb7185'],
          borderRadius: 6,
          borderWidth: 0,
        },
      ],
    }),
    [sums]
  );

  const top5Users = useMemo(() => topUsersByTotal.slice(0, 5), [topUsersByTotal]);

  const stackedCompositionData = useMemo(
    () => ({
      labels: top5Users.map(userLabel),
      datasets: [
        { label: 'Read', data: top5Users.map((r) => pickNum(r, 'read')), backgroundColor: '#38bdf8', stack: 'x' },
        { label: 'Sent', data: top5Users.map((r) => pickNum(r, 'sent')), backgroundColor: '#a78bfa', stack: 'x' },
        { label: 'Delivered', data: top5Users.map((r) => pickNum(r, 'delivered')), backgroundColor: ACCENT, stack: 'x' },
        { label: 'Failed', data: top5Users.map((r) => pickNum(r, 'failed')), backgroundColor: '#f87171', stack: 'x' },
        { label: 'Pending', data: top5Users.map((r) => pickNum(r, 'pending')), backgroundColor: '#fbbf24', stack: 'x' },
        {
          label: 'Del. failed',
          data: top5Users.map((r) => pickNum(r, 'delivery_failed')),
          backgroundColor: '#fb7185',
          stack: 'x',
        },
      ],
    }),
    [top5Users]
  );

  const stackedBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: '#64748b', boxWidth: 10, font: { size: 10 } },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.dataset.label}: ${formatInt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          grid: { color: CHART_MUTED },
          ticks: { color: '#64748b' },
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
      },
    }),
    []
  );

  const barOptionsHorizontal = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${formatInt(ctx.raw)} messages`,
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: CHART_MUTED },
          ticks: { color: '#64748b', maxTicksLimit: 8 },
        },
        y: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
      },
    }),
    []
  );

  const barOptionsVertical = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => formatInt(ctx.raw),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: CHART_MUTED },
          ticks: { color: '#64748b', maxTicksLimit: 8 },
        },
      },
    }),
    []
  );

  const doughnutStatusOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '58%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: '#64748b', boxWidth: 10, padding: 12, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.label}: ${formatInt(ctx.raw)}`,
          },
        },
      },
    }),
    []
  );

  const fetchStats = useCallback(async (nextPage, nextPeriod) => {
    setError('');
    setLoading(true);
    try {
      const data = await getMessageStatsAll({
        period: nextPeriod,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      setRows(Array.isArray(data?.data) ? data.data : []);
      const p = data?.pagination;
      setTotalPages(p?.total_pages || 1);
      setTotalRecords(p?.total_records ?? 0);
      setPage(nextPage);
    } catch (e) {
      setError(e?.message || 'Failed to load message stats');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(1, period);
  }, [period, fetchStats]);

  const onPeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  const goPrev = () => {
    if (page <= 1 || loading) return;
    fetchStats(page - 1, period);
  };

  const goNext = () => {
    if (page >= totalPages || loading) return;
    fetchStats(page + 1, period);
  };

  const pageScoped = totalRecords > rows.length && rows.length > 0;

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faChartPie} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Administration</span>
            <h1 className="admin-page__title">Overview</h1>
            <p className="admin-page__subtitle">
              Message delivery stats across all users (up to {PAGE_SIZE} accounts per request). Charts reflect the loaded
              rows; use pagination if there are more than {PAGE_SIZE} accounts.
            </p>
          </div>
        </div>
      </header>

      <div className="admin-overview-dash dashboard-wrap">
        {pageScoped && (
          <p className="admin-overview-dash__hint">
            Totals in the strip and charts are sums for <strong>this page only</strong> ({rows.length} of{' '}
            {formatInt(totalRecords)} accounts). Paginate to include others.
          </p>
        )}

        <div className="dashboard-info-bar">
          <div className="dashboard-info-header">
            <span className="dashboard-info-title">Period totals (this page)</span>
          </div>
          <div className="dashboard-info-grid admin-overview-kpi-grid">
            <div className="dashboard-info-item dashboard-info-item--highlight">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faChartColumn} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Total messages</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.total)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faEye} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Read</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.read)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faPaperPlane} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Sent</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.sent)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faCircleCheck} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Delivered</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.delivered)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Failed</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.failed)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faClock} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Pending</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.pending)}</span>
              </div>
            </div>
            <div className="dashboard-info-item">
              <span className="dashboard-info-icon">
                <FontAwesomeIcon icon={faEnvelopeOpenText} />
              </span>
              <div className="dashboard-info-meta">
                <span className="dashboard-info-label">Delivery failed</span>
                <span className="dashboard-info-value">{loading ? '…' : formatInt(sums.delivery_failed)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-cards-row">
          <OverviewStatCard
            title="Total volume"
            value={loading ? '…' : formatInt(sums.total)}
            subtitle="Delivered vs rest (this page)"
            segments={doughnutSafe(sums.delivered, Math.max(0, sums.total - sums.delivered))}
          />
          <OverviewStatCard
            title="Read"
            value={loading ? '…' : formatInt(sums.read)}
            subtitle="Share of all messages"
            segments={doughnutSafe(sums.read, Math.max(0, sums.total - sums.read))}
          />
          <OverviewStatCard
            title="Sent + pending"
            value={loading ? '…' : formatInt(pipeline)}
            subtitle="In flight vs rest"
            segments={doughnutSafe(pipeline, Math.max(0, sums.total - pipeline))}
          />
          <OverviewStatCard
            title="Failures"
            value={loading ? '…' : formatInt(failures)}
            subtitle="Failed + delivery failed"
            segments={doughnutSafe(failures, Math.max(0, sums.total - failures))}
          />
        </div>

        <div className="dashboard-charts-row">
          <div className="dashboard-chart-card dashboard-chart-line">
            <h3>Top accounts by volume (this page)</h3>
            <div className="dashboard-chart-inner" style={{ minHeight: 220 }}>
              {!loading && topUsersByTotal.length ? (
                <Bar data={userBarData} options={barOptionsHorizontal} />
              ) : (
                <p className="dashboard-no-data" style={{ paddingTop: 48 }}>
                  {loading ? 'Loading chart…' : 'No rows to chart.'}
                </p>
              )}
            </div>
          </div>
          <div className="dashboard-chart-card dashboard-chart-doughnut">
            <h3>Status mix (this page)</h3>
            <div className="dashboard-chart-inner">
              {!loading && sums.total > 0 ? (
                <Doughnut data={statusDoughnutData} options={doughnutStatusOptions} />
              ) : (
                <p className="dashboard-no-data" style={{ paddingTop: 48 }}>
                  {loading ? 'Loading chart…' : 'No messages on this page.'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-bottom-charts">
          <div className="dashboard-bottom-chart">
            <h3>Breakdown by status</h3>
            <div className="dashboard-bottom-chart-inner">
              {!loading && sums.total > 0 ? (
                <Bar data={statusBarData} options={barOptionsVertical} />
              ) : (
                <p className="dashboard-no-data" style={{ paddingTop: 80 }}>
                  {loading ? 'Loading chart…' : 'No data.'}
                </p>
              )}
            </div>
          </div>
          <div className="dashboard-bottom-chart">
            <h3>Top accounts — status composition</h3>
            <div className="dashboard-bottom-chart-inner">
              {!loading && top5Users.length ? (
                <Bar data={stackedCompositionData} options={stackedBarOptions} />
              ) : (
                <p className="dashboard-no-data" style={{ paddingTop: 80 }}>
                  {loading ? 'Loading chart…' : 'Need at least one row with data.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>Message stats</h2>
          <p>
            {loading ? 'Loading…' : `${totalRecords} record(s) · page ${page} of ${totalPages}`}
          </p>
        </div>
        <div className="admin-panel__body">
          {error && (
            <div className="admin-feedback admin-feedback--error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
          <div className="admin-field" style={{ marginBottom: 20 }}>
            <label className="admin-label" htmlFor="message-stats-period">
              Period
            </label>
            <select
              id="message-stats-period"
              className="admin-select"
              value={period}
              onChange={onPeriodChange}
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="admin-panel__body" style={{ padding: 0 }}>
          <div className="admin-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Total</th>
                  <th>Read</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Failed</th>
                  <th>Pending</th>
                  <th>Delivery failed</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="admin-empty">Loading stats…</td>
                  </tr>
                ) : (
                  <>
                    {rows.map((row, idx) => (
                      <tr key={row.user_id != null ? String(row.user_id) : `row-${idx}`}>
                        <td>{row.user_id ?? '—'}</td>
                        <td>{row.name || '—'}</td>
                        <td>{row.email || '—'}</td>
                        <td>{formatInt(row.total)}</td>
                        <td>{formatInt(row.read)}</td>
                        <td>{formatInt(row.sent)}</td>
                        <td>{formatInt(row.delivered)}</td>
                        <td>{formatInt(row.failed)}</td>
                        <td>{formatInt(row.pending)}</td>
                        <td>{formatInt(row.delivery_failed)}</td>
                      </tr>
                    ))}
                    {!rows.length && (
                      <tr>
                        <td colSpan="10" className="admin-empty">No stats for this period.</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className="admin-pagination">
            <span className="admin-pagination__meta">
              Page {page} of {totalPages}
            </span>
            <div className="admin-pagination__controls">
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                disabled={loading || page <= 1}
                onClick={goPrev}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Prev
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                disabled={loading || page >= totalPages}
                onClick={goNext}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MessageStatsOverview;
